import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';

import { GlobalStyles } from '@vimeo/core/components/GlobalStyles';
import { ThemeProvider } from '@vimeo/core/components/ThemeProvider';
import { useIsKeyboardOpen } from '@vimeo/live-core/hooks/dom/useIsKeyboardOpen';
import { useIsPortraitScreenOrientation } from '@vimeo/live-core/hooks/dom/useIsPortraitScreenOrientation';
import { Maybe } from '@vimeo/core/types/generic';
import { GctlProvider } from '@vimeo/gctl-api/hooks/GctlProvider';
import { EComposerSessionType } from '@vimeo/live-core/types/composer_session';
import { InteractionToolsEntry, InteractionToolsRegistrantObserver } from '@vimeo/live-interaction';
import { useViewer } from '@vimeo/viewer';

import { useGlobalStore } from '../../lead-capture-form/state/globalStore';
import { withViewer } from '../../wrapper/withViewer';
import { InteractionToolsContainerDesktop, InteractionToolsContainerMobile } from './styles';

interface InteractivityToolProps {
  isMobileView: boolean;
  parentContainerRef: HTMLElement;
  sessionId: number | string;
  sessionType: EComposerSessionType.LIVE_EVENT | EComposerSessionType.UNKNOWN;
  toolbarAttachPosition: 'bottom' | 'right';
}

const getIsActiveStatus = () => {
  const { hasNewLeadCaptureFlow } = useGlobalStore.getState();
  return !!hasNewLeadCaptureFlow;
};

const InteractivityTool = (props: InteractivityToolProps) => {
  const { parentContainerRef, toolbarAttachPosition } = props;
  const router = useRouter();
  const unlistedHash = router.query['unlisted_hash'] as Maybe<string>;
  const rawRoomAuthDisabledOptions = router.query['auth_room_disabled_options'] as Maybe<string>;
  const viewer = useViewer();
  const isKeyboardOpened = useIsKeyboardOpen();
  const isPortrait = useIsPortraitScreenOrientation();
  const registrantObserverSessionId = useGlobalStore((store) => store.entityId);
  const InteractionToolsContainer = useMemo(
    () => (props.isMobileView ? InteractionToolsContainerMobile : InteractionToolsContainerDesktop),
    [props.isMobileView],
  );

  const roomAuthDisabledOptions: Array<string> = useMemo(() => {
    return rawRoomAuthDisabledOptions
      ? String(rawRoomAuthDisabledOptions)
          .split(',')
          .map((it) => it.trim())
      : [];
  }, [rawRoomAuthDisabledOptions]);

  useEffect(() => {
    if (!parentContainerRef) {
      return;
    }

    if (toolbarAttachPosition === 'bottom') {
      parentContainerRef.style.flexDirection = 'column';
    } else {
      parentContainerRef.style.flexDirection = 'row';
    }
  }, [parentContainerRef, toolbarAttachPosition]);

  return useMemo(
    () =>
      createPortal(
        <GctlProvider
          xVimeoPage={window.location.pathname}
          jwt={viewer.jwt || window['jwtToken']}
          baseUrl={`https://${viewer?.apiUrl}` || window['entityData']['base_url']}
          locale={viewer?.locale}
        >
          <ThemeProvider defaultTheme={'dark'} hasThemeSupport={true}>
            <GlobalStyles />

            <InteractionToolsContainer
              isKeyboardOpened={isKeyboardOpened}
              isPortrait={isPortrait}
              toolbarAttachPosition={toolbarAttachPosition}
            >
              <InteractionToolsRegistrantObserver
                isActive={getIsActiveStatus()}
                sessionId={registrantObserverSessionId}
              >
                <InteractionToolsEntry
                  isCollapsibleSignIn={true}
                  isCustomThemeEnabled={true}
                  isFooterEnabled={false}
                  isManagementDisabled={true}
                  sessionId={props.sessionId}
                  sessionType={props.sessionType}
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  toolbarAttachPosition={toolbarAttachPosition}
                  roomUnlistedHash={unlistedHash}
                  roomDisabledOptions={roomAuthDisabledOptions}
                />
              </InteractionToolsRegistrantObserver>
            </InteractionToolsContainer>
          </ThemeProvider>
        </GctlProvider>,
        parentContainerRef,
      ),
    [
      InteractionToolsContainer,
      isKeyboardOpened,
      isPortrait,
      parentContainerRef,
      props.sessionId,
      props.sessionType,
      registrantObserverSessionId,
      toolbarAttachPosition,
      unlistedHash,
      roomAuthDisabledOptions,
      viewer,
    ],
  );
};

export default withViewer<InteractivityToolProps>(InteractivityTool, { hideSpinner: true });
